import React, { useState } from 'react';
import { Collapse, Drawer, styled } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { Colors } from 'library';
import { Settings, ExpandMore, ExpandLess, Help } from '@mui/icons-material';

export type AppMode = 'recruit' | 'subs';

export const SIDEBAR_WIDTH = 220;

export interface NavItem {
    label: string;
    icon: React.ReactNode;
    route: string;
    children?: NavItem[];
}

interface ModeToggleConfig {
    mode: AppMode;
    onModeChange: (mode: AppMode) => void;
}

interface LayoutSidebarProps {
    small: boolean;
    open: boolean;
    onClose: () => void;
    navItems: NavItem[];
    bottomNavItems?: NavItem[];
    settingsNavItems?: NavItem[];
    modeToggle?: ModeToggleConfig;
    extraBottomContent?: React.ReactNode;
    showBottomSection?: boolean;
}

export function LayoutSidebar({
    small,
    open,
    onClose,
    navItems,
    bottomNavItems,
    settingsNavItems,
    modeToggle,
    extraBottomContent,
    showBottomSection = true,
}: LayoutSidebarProps) {
    const location = useLocation();
    const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith('/recruit/settings'));

    const isActive = (route: string) => {
        if (route === '/recruit' || route === '/home' || route === '/recruit-candidate') {
            return location.pathname === route;
        }
        return location.pathname.startsWith(route);
    };

    const handleNavClick = () => {
        if (small) onClose();
    };

    return (
        <StyledDrawer
            variant={small ? 'temporary' : 'persistent'}
            anchor="left"
            open={open}
            onClose={onClose}
            small={small}
        >
            <SidebarContent>
                <TopSection>
                    {modeToggle && (
                        <ModeToggle>
                            <ModeButton
                                active={modeToggle.mode === 'recruit'}
                                onClick={() => modeToggle.onModeChange('recruit')}
                            >
                                Recruit
                            </ModeButton>
                            <ModeButton
                                active={modeToggle.mode === 'subs'}
                                onClick={() => modeToggle.onModeChange('subs')}
                            >
                                Subs
                            </ModeButton>
                        </ModeToggle>
                    )}

                    <NavSection>
                        {navItems?.map((item) => (
                            <NavLink
                                key={item.label}
                                to={item.route}
                                onClick={handleNavClick}
                                active={isActive(item.route)}
                            >
                                <NavIcon active={isActive(item.route)}>{item.icon}</NavIcon>
                                <NavLabel active={isActive(item.route)}>{item.label}</NavLabel>
                            </NavLink>
                        ))}

                        {settingsNavItems && settingsNavItems.length > 0 && (
                            <>
                                <SettingsToggle onClick={() => setSettingsOpen(!settingsOpen)}>
                                    <NavIcon active={location.pathname.startsWith('/recruit/settings')}>
                                        <Settings />
                                    </NavIcon>
                                    <NavLabel active={location.pathname.startsWith('/recruit/settings')}>
                                        Settings
                                    </NavLabel>
                                    <ExpandIcon>
                                        {settingsOpen ? (
                                            <ExpandLess fontSize="small" />
                                        ) : (
                                            <ExpandMore fontSize="small" />
                                        )}
                                    </ExpandIcon>
                                </SettingsToggle>

                                <Collapse in={settingsOpen}>
                                    {settingsNavItems?.map((item) => (
                                        <SettingsEntry
                                            key={item.label}
                                            item={item}
                                            isActive={isActive}
                                            onNavClick={handleNavClick}
                                        />
                                    ))}
                                </Collapse>
                            </>
                        )}
                    </NavSection>

                    {bottomNavItems && bottomNavItems.length > 0 && (
                        <>
                            <Divider />
                            <NavSection>
                                {bottomNavItems?.map((item) => (
                                    <NavLink
                                        key={item.label}
                                        to={item.route}
                                        onClick={handleNavClick}
                                        active={isActive(item.route)}
                                    >
                                        <NavIcon active={isActive(item.route)}>{item.icon}</NavIcon>
                                        <NavLabel active={isActive(item.route)}>{item.label}</NavLabel>
                                    </NavLink>
                                ))}
                            </NavSection>
                        </>
                    )}
                </TopSection>

                {showBottomSection && (
                    <BottomSection>
                        <Divider />
                        <NavSection>
                            <ExternalLink
                                href="https://chatwoot.join-tandem.com/hc/child-care-center-help-center/en"
                                target="_blank"
                            >
                                <NavIcon active={false}>
                                    <Help />
                                </NavIcon>
                                <NavLabel active={false}>FAQ</NavLabel>
                            </ExternalLink>
                        </NavSection>
                        {extraBottomContent}
                    </BottomSection>
                )}
            </SidebarContent>
        </StyledDrawer>
    );
}

function SettingsEntry({
    item,
    isActive,
    onNavClick,
}: {
    item: NavItem;
    isActive: (route: string) => boolean;
    onNavClick: () => void;
}) {
    const children = item.children ?? [];
    const hasChildren = children.length > 0;
    const childActive = children.some((child) => isActive(child.route));
    const [open, setOpen] = useState(childActive);

    if (!hasChildren) {
        return (
            <NavLink to={item.route} onClick={onNavClick} active={isActive(item.route)} nested>
                <NavIcon active={isActive(item.route)} nested>
                    {item.icon}
                </NavIcon>
                <NavLabel active={isActive(item.route)} small>
                    {item.label}
                </NavLabel>
            </NavLink>
        );
    }

    return (
        <>
            <SubGroupToggle onClick={() => setOpen(!open)}>
                <NavIcon active={childActive} nested>
                    {item.icon}
                </NavIcon>
                <NavLabel active={childActive} small>
                    {item.label}
                </NavLabel>
                <ExpandIcon>{open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</ExpandIcon>
            </SubGroupToggle>
            <Collapse in={open}>
                {children.map((child) => (
                    <NavLink
                        key={child.label}
                        to={child.route}
                        onClick={onNavClick}
                        active={isActive(child.route)}
                        deep
                    >
                        <NavIcon active={isActive(child.route)} nested>
                            {child.icon}
                        </NavIcon>
                        <NavLabel active={isActive(child.route)} small>
                            {child.label}
                        </NavLabel>
                    </NavLink>
                ))}
            </Collapse>
        </>
    );
}

const StyledDrawer = styled(Drawer)<{ small: boolean }>(({ small }) => ({
    '& .MuiDrawer-paper': {
        width: SIDEBAR_WIDTH,
        backgroundColor: Colors.white,
        borderRight: `1px solid ${Colors.border}`,
        top: small ? 50 : 60,
        height: small ? 'calc(100vh - 50px)' : 'calc(100vh - 60px)',
        zIndex: 1200,
    },
}));

const SidebarContent = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    overflowY: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
});

const TopSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});

const BottomSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 8,
});

const ModeToggle = styled('div')({
    display: 'flex',
    margin: '16px 16px 8px',
    backgroundColor: Colors.fill,
    borderRadius: 20,
    padding: 3,
});

const ModeButton = styled('button')<{ active: boolean }>(({ active }) => ({
    flex: 1,
    padding: '8px 0',
    border: 'none',
    borderRadius: 18,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'all 0.2s ease',
    backgroundColor: active ? Colors.primary : 'transparent',
    color: active ? Colors.white : Colors.primary,
    outline: 'none',
    boxShadow: 'none',
    '&:focus, &:focus-visible': {
        outline: 'none',
        boxShadow: 'none',
    },
}));

const NavSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 8px',
});

const NavLink = styled(Link)<{ active: boolean; nested?: boolean; deep?: boolean }>(({ active, nested, deep }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: deep ? '6px 8px 6px 40px' : nested ? '6px 8px 6px 24px' : '8px 8px',
    borderRadius: 6,
    textDecoration: 'none',
    cursor: 'pointer',
    backgroundColor: active ? Colors.fill : 'transparent',
    '&:hover': {
        backgroundColor: Colors.fill,
    },
}));

const SettingsToggle = styled('button')({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 8px',
    borderRadius: 6,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    width: '100%',
    '&:hover': {
        backgroundColor: Colors.fill,
    },
    '&:focus, &:focus-visible': {
        outline: 'none',
    },
});

const SubGroupToggle = styled('button')({
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px 6px 24px',
    borderRadius: 6,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    width: '100%',
    '&:focus, &:focus-visible': {
        outline: 'none',
    },
    '&:hover': {
        backgroundColor: Colors.border,
    },
});

const NavIcon = styled('span')<{ active: boolean; nested?: boolean }>(({ active, nested }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    marginRight: 10,
    '& .MuiSvgIcon-root': {
        fontSize: nested ? 18 : 20,
        color: active ? Colors.primary : Colors.textSecondary,
    },
}));

const NavLabel = styled('span')<{ active: boolean; small?: boolean }>(({ active, small: isSmall }) => ({
    fontSize: isSmall ? 13 : 14,
    fontWeight: active ? 700 : 400,
    color: Colors.primary,
}));

const ExpandIcon = styled('span')({
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    color: Colors.textSecondary,
});

const Divider = styled('div')({
    height: 1,
    backgroundColor: Colors.fill,
    margin: '4px 16px',
});

const ExternalLink = styled('a')({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 8px',
    borderRadius: 6,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: Colors.fill,
    },
});
